import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import WheelOfLifeEvolution from './WheelOfLifeEvolution';
import ManualQuestionnaireModal from '../counselor/ManualQuestionnaireModal';
import CustomerReportTemplate from './CustomerReportTemplate';

const BaselineEvolutionReport = ({ client }) => {
    const [showManualModal, setShowManualModal] = useState(false);
    const [assessmentType, setAssessmentType] = useState('checkpoint');
    const [showReport, setShowReport] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [baselines, setBaselines] = useState([]);
    const [toolkitDocs, setToolkitDocs] = useState([]);

    useEffect(() => {
        loadExperiences();
        loadBaselines();
        loadToolkitDocs();
        // Run migration on first load
        DataManager.migrateOldBaselines();
    }, [client.id]);

    const loadExperiences = async () => {
        const allWorks = await DataManager.getClientWorks(client.id, true);
        const exp = allWorks.filter(w => ['frequency_feedback', 'dream_analysis'].includes(w.type));
        setExperiences(exp);
    };

    const loadBaselines = async () => {
        const all = await DataManager.getAllBaselines(client.id);
        setBaselines(all || []);
    };

    const loadToolkitDocs = async () => {
        const allWorks = await DataManager.getClientWorks(client.id, true);
        const docs = allWorks.filter(w => w.type === 'toolkit_document');
        setToolkitDocs(docs);
    };

    const openQuestionnaireModal = (type = 'checkpoint') => {
        setAssessmentType(type);
        setShowManualModal(true);
    };

    const handleModalClose = () => {
        setShowManualModal(false);
        loadBaselines();
    };

    return (
        <div className="space-y-6 animate-fade-in">

            <div className="flex justify-between items-center bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
                <div className="flex items-center gap-4">
                    <img src="/logo.png" alt="Logo" className="h-10 object-contain brightness-105" />
                    <div>
                        <h2 className="text-2xl font-black text-slate-800 tracking-tight">AwarenessTool <span className="text-slate-300 font-light text-xl mx-1">|</span> Evoluzione & Baseline</h2>
                        <p className="text-xs font-black text-slate-400 uppercase tracking-widest mt-1 opacity-70">Monitoraggio compilazioni e ruota della vita</p>
                    </div>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => openQuestionnaireModal('checkpoint')}
                        className="px-5 py-3 bg-indigo-600 text-white rounded-2xl font-bold text-sm hover:bg-indigo-700 transition shadow-lg flex items-center gap-2"
                    >
                        <span>📝</span> Nuova Compilazione
                    </button>
                </div>
            </div>

            {/* Baselines Overview */}
            {baselines.length > 0 && (
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 bg-indigo-400 rounded-full"></span>
                        Storico Compilazioni ({baselines.length})
                    </h3>
                    <div className="flex flex-wrap gap-3">
                        {baselines
                            .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
                            .map((b, idx) => {
                                const typeLabels = {
                                    'T0': { label: 'Primo colloquio', icon: '🌱', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
                                    'T1': { label: 'Intermedio', icon: '🌿', color: 'bg-blue-50 border-blue-200 text-blue-700' },
                                    'T2': { label: 'Chiusura', icon: '🏁', color: 'bg-amber-50 border-amber-200 text-amber-700' },
                                    'checkpoint': { label: 'Checkpoint', icon: '📊', color: 'bg-indigo-50 border-indigo-200 text-indigo-700' }
                                };
                                const info = typeLabels[b.baselineType] || typeLabels.checkpoint;
                                return (
                                    <div key={b.id || idx} className={`px-4 py-3 rounded-2xl border text-xs font-bold ${info.color}`}>
                                        <div className="flex items-center gap-2">
                                            <span>{info.icon}</span>
                                            <span>{info.label}</span>
                                        </div>
                                        <div className="text-[10px] mt-1 opacity-70">
                                            {new Date(b.timestamp).toLocaleDateString('it-IT')}
                                        </div>
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            )}

            {/* Toolkit Documents / Photos */}
            {toolkitDocs.length > 0 && (
                <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
                    <h3 className="text-sm font-black text-slate-400 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                        <span className="w-2 h-2 bg-brand-400 rounded-full"></span>
                        Documenti & Foto Raccolti ({toolkitDocs.length})
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {toolkitDocs.map(doc => (
                            <div key={doc.id} className="relative group rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition bg-slate-50">
                                {doc.content?.photoData ? (
                                    <img src={doc.content.photoData} alt={doc.content.photoName} className="w-full h-32 object-cover" />
                                ) : (
                                    <div className="w-full h-32 flex items-center justify-center bg-brand-50 text-brand-500 text-3xl">📄</div>
                                )}
                                <div className="p-3">
                                    <p className="text-[10px] font-bold text-gray-700 truncate">{doc.content?.documentTitle || doc.content?.photoName || 'Documento'}</p>
                                    <p className="text-[8px] text-gray-400 mt-1">{new Date(doc.timestamp).toLocaleDateString('it-IT')}</p>
                                </div>
                                <div className="absolute inset-0 bg-brand-900/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                     <button 
                                        onClick={() => {
                                            if (doc.content?.photoData) {
                                                const win = window.open();
                                                win.document.write(`<img src="${doc.content.photoData}" style="max-width:100%"/>`);
                                            } else {
                                                alert(`Documento: ${doc.content?.documentTitle || 'Documento allegato'}`);
                                            }
                                        }}
                                        className="px-3 py-1 bg-white text-brand-900 rounded-lg text-[10px] font-bold"
                                     >
                                         Ingrandisci
                                     </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Manual Modal */}
            {showManualModal && (
                <ManualQuestionnaireModal
                    client={client}
                    assessmentType={assessmentType}
                    onClose={handleModalClose}
                    onSave={handleModalClose}
                />
            )}

            {/* Customer Report Modal */}
            {showReport && (
                <CustomerReportTemplate
                    client={client}
                    onClose={() => setShowReport(false)}
                />
            )}

            {/* Wheel of Life Section */}
            <div className="bg-white p-2 rounded-3xl shadow-sm border border-gray-100">
                <WheelOfLifeEvolution client={client} />
            </div>
        </div>
    );
};

export default BaselineEvolutionReport;
